import React, { useEffect, useState } from 'react';
import IDocumentFile from '../interfaces/IDocumentFile';
import { GridRowsProp, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Badge, Box, Button, CircularProgress, IconButton, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HAppRoutes from '../../../app/features/routes/helpers/HAppRoutes';
import langDocumentFilesTable from '../langs/langDocumentFilesTable';
import AppCustomIcon from '../../../app/features/icon/AppIcon';
import AppFormatDate from '../../../app/features/format/AppFormatDate';
import useDocumentTypes from '../hooks/useDocumentTypes';
import langDocumentType from '../langs/langDocumentType';
import CAppDataGrid from '../../../app/components/CAppDataGrid';
import useDocumentFile from '../hooks/useDocumentFile';
import { WarningOutlined } from '@mui/icons-material';
import CAppTooltip from '../../../app/components/CAppTooltip';
import DocumentRoutes from '../DocumentRoutes';
import fileDownload from 'js-file-download';
import JSZip from 'jszip';
import moment from 'moment';
import _ from 'lodash';
import langDocumentFiles from '../langs/langDocumentFiles';
import useAxios from '../../../app/hooks/useAxios';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import CDocumentDownloadErrorSnackbar from '../components/CDocumentDownloadErrorSnackbar';
import styleVariables from '../../../app/styles/styleVariables';
import useInvestExpertises from '../../investor/hooks/expertise/useInvestExpertises';
import { langLookup } from '../../../app/utils/langLookup';

type VDocumentFilesTableDesktopV2Props = {
	documents: IDocumentFile[];
	displayColumns?: string[];
	hideFooter?: boolean;
};

const VDocumentFilesTableDesktopV2 = (props: VDocumentFilesTableDesktopV2Props) => {
	const { documents, displayColumns, hideFooter } = props;

	const pageSize = 10;

	const { get } = useAxios();
	const auth = useAuthContext();

	const navigate = useNavigate();
	const { t } = useTranslation();

	const { documentTypes } = useDocumentTypes();
	const investExpertises = useInvestExpertises();

	const [rows, setRows] = useState<GridRowsProp>();

	useEffect(() => {
		if (documents && documentTypes && investExpertises) {
			setRows(
				documents.map((x) => {
					const documentType = documentTypes.find((t) => t.tag === x.documentType);
					return {
						...x,
						['fund.name']: x.fund?.name,
						fundTypeName: investExpertises?.find((e) => e.tag === x.fund?.typeTag)?.name,
						['company.name']: x.company?.name,
						isRealized: x.fund?.isRealized,
						isDownloading: false,
						isDownloadingError: false,
						documentTypeName:
							documentType && langLookup(langDocumentType, documentType.name)
								? t(langLookup(langDocumentType, documentType.name)?.tag ?? '')
								: '',
					};
				}),
			);
		}
	}, [documents, documentTypes, investExpertises, t]);

	const [selectDocumentId, setSelectDocumentId] = useState<number | null>(null);
	const { document: currentDocument, download } = useDocumentFile(selectDocumentId);

	const handleDownload = (id: number) => {
		setSelectDocumentId(id);
	};

	const [downloadError, setDownloadError] = useState<Error | null>(null);

	useEffect(() => {
		if (currentDocument) {
			setRows((prevData) =>
				prevData?.map((x) => {
					if (x.id === currentDocument.id) {
						x.isDownloading = true;
					}
					return x;
				}),
			);
			download()
				.then(() => {
					setRows((prevData) =>
						prevData?.map((x) => {
							if (x.id === currentDocument.id) {
								x.isDownloading = false;
							}
							return x;
						}),
					);
				})
				.catch((e) => {
					setDownloadError(e);
					setRows((prevData) =>
						prevData?.map((x) => {
							if (x.id === currentDocument.id) {
								x.isDownloadingError = true;
							}
							return x;
						}),
					);
				})
				.finally(() => {
					setSelectDocumentId(null);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentDocument]);

	const handleView = (id: number) => {
		navigate(HAppRoutes.getPath(DocumentRoutes.document.tag, { id: id.toString() }));
	};

	const columns: GridColDef[] = [
		{
			field: 'fund.name',
			headerName: t(langDocumentFilesTable.fund.tag) as string,
			width: 400,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'fundTypeName',
			headerName: t(langDocumentFilesTable.fund_type.tag) as string,
			width: 200,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => t(params.value),
		},
		{
			field: 'name',
			headerName: t(langDocumentFilesTable.document_name.tag) as string,
			minWidth: 250,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => (
				<Link
					sx={{
						textDecoration: 'none',
						color: params.row.fund?.isRealized ? styleVariables.greyColor : '',
						'&:hover': {
							textDecoration: 'underline',
						},
					}}
					onClick={() => handleView(params.row.id)}
				>
					<CAppTooltip title={params.value} placement="right" disableInteractive>
						<span>
							<Badge variant="dot" color="secondary" overlap="circular" invisible={!params.row.isNew}>
								<AppCustomIcon tag={`document_format_${params.row.formatTag}`} />
							</Badge>
							&nbsp;{params.value}
						</span>
					</CAppTooltip>
				</Link>
			),
		},
		{
			field: 'documentTypeName',
			headerName: t(langDocumentFilesTable.document_type.tag) as string,
			minWidth: 250,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'company.name',
			headerName: t(langDocumentFilesTable.subportfolio.tag) as string,
			width: 250,
			align: 'left',
			headerAlign: 'left',
			hide: true,
		},
		{
			field: 'date',
			headerName: t(langDocumentFilesTable.release_date.tag) as string,
			width: 140,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => <AppFormatDate date={params.value} />,
		},
		{
			field: 'id',
			headerName: t(langDocumentFilesTable.download.tag) as string,
			width: 110,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => (
				<>
					<IconButton
						color="primary"
						disabled={params.row.isDownloading || params.row.isDownloadingError}
						onClick={() => handleDownload(params.value)}
						sx={{ cursor: 'pointer' }}
					>
						{params.row.isDownloadingError ? (
							<WarningOutlined />
						) : params.row.isDownloading ? (
							<CircularProgress color="secondary" size={26} />
						) : (
							<AppCustomIcon tag="download" />
						)}
					</IconButton>
				</>
			),
		},
	];

	const [pageNumber, setPageNumber] = useState(0);
	useEffect(() => {
		// Clear documentsSelected when change page number
		setDocumentSelected([]);
	}, [pageNumber]);

	const [sortedRowIds, setSortedRowIds] = useState([]);

	const [documentsSelected, setDocumentSelected] = useState<IDocumentFile[]>([]);
	const handleChangeSelectionModel = (newSelectionModel: number[]) => {
		let documentsTmp = _.cloneDeep(documents);

		documentsTmp = _.sortBy(documentsTmp, (x) => _.findIndex(sortedRowIds, (y) => x.id === y));

		if (newSelectionModel.length === rows?.length) {
			// Select all from pageNumber
			if (newSelectionModel.length > pageSize) {
				setDocumentSelected(
					documentsTmp
						.filter((x) => newSelectionModel.includes(x.id))
						.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize),
				);
			} else {
				setDocumentSelected(documentsTmp.filter((x) => newSelectionModel.includes(x.id)));
			}
		} else {
			setDocumentSelected(documentsTmp.filter((x) => newSelectionModel.includes(x.id)));
		}
	};

	const [isDownloading, setIsDownloading] = useState(false);
	const handleDownloadMultiple = () => {
		if (documentsSelected && documentsSelected.length > 0) {
			const fileName = moment().format('YYYY-MM-DD') + ' - DOCUMENTS';
			const zipFile = new JSZip();
			const filesZip = zipFile.folder(fileName);

			const promisesDownload: Promise<{ content: JSZip.InputType; fileName: string }>[] = [];
			documentsSelected.forEach((document) => {
				promisesDownload.push(
					get(
						auth.authOverrideUserId
							? `/download/document/${document.id.toString()}?userId=${auth.authOverrideUserId}`
							: `/download/document/${document.id.toString()}`,
						{
							responseType: 'blob',
						},
					).then((response) => {
						return {
							content: response.data,
							fileName: document.name,
						};
					}),
				);
			});

			setIsDownloading(true);
			return Promise.all(promisesDownload)
				.then((responses) => {
					// Clean response and deduplicate by adding (count) at the end of the file
					const fileNamesCount: { fileName: string; count: number }[] = [];
					responses.forEach((response) => {
						const fileNamesCountItem = fileNamesCount.find((x) => x.fileName === response.fileName);
						if (fileNamesCountItem) {
							fileNamesCountItem.count += 1;
							const partDots = response.fileName.split('.');
							const ext = partDots[partDots.length - 1];
							const name = response.fileName.slice(0, response.fileName.length - ext.length - 1);
							response.fileName = name + ' (' + fileNamesCountItem.count + ').' + ext;
						} else {
							fileNamesCount.push({
								fileName: response.fileName,
								count: 1,
							});
						}
					});
					responses.forEach((response) => {
						filesZip?.file(response.fileName, response.content);
					});
					zipFile.generateAsync({ type: 'blob' }).then((content) => {
						fileDownload(content, fileName + '.zip');
					});
				})
				.finally(() => {
					setDocumentSelected([]);
					setIsDownloading(false);
				});
		}
	};

	return (
		<>
			{rows && (
				<Box
					sx={{
						position: 'relative',
						marginTop: documentsSelected && documentsSelected.length > 0 ? '70px' : '0px',
					}}
				>
					{documentsSelected && documentsSelected.length > 0 ? (
						<Box sx={{ position: 'absolute', zIndex: 11000, top: '-60px', right: '0px', width: 'auto' }}>
							<Button
								startIcon={
									isDownloading ? <CircularProgress size={20} /> : <AppCustomIcon tag="download" />
								}
								disabled={isDownloading}
								sx={{ marginBottom: '10px' }}
								variant="outlined"
								color="primary"
								size="small"
								onClick={() => handleDownloadMultiple()}
							>
								{isDownloading
									? t(langDocumentFiles.text_downloading.tag)
									: t(langDocumentFiles.download_x_files.tag, { count: documentsSelected.length })}
							</Button>
						</Box>
					) : (
						''
					)}
					<CAppDataGrid
						className="document-files-desktop"
						rows={rows}
						columns={columns}
						displayColumns={displayColumns}
						pageSize={pageSize}
						autoHeight
						disableSelectionOnClick
						disableColumnSelector
						disableColumnMenu
						rowsPerPageOptions={[10]}
						checkboxSelection
						onSelectionModelChange={(newSelectionModel) =>
							handleChangeSelectionModel(newSelectionModel as number[])
						}
						selectionModel={documentsSelected ? documentsSelected.map((x) => x.id) : []}
						onPageChange={(page) => setPageNumber(page)}
						onSortModelChange={() => setDocumentSelected([])}
						onStateChange={(state) => setSortedRowIds(state.sorting.sortedRows)}
						hideFooter={hideFooter ?? false}
					/>

					<CDocumentDownloadErrorSnackbar
						downloadError={downloadError}
						onClose={() => setDownloadError(null)}
					/>
				</Box>
			)}
		</>
	);
};

export default VDocumentFilesTableDesktopV2;
